import { AssessmentSelectDto, TechnologyTimeline } from '@@types/api.types';
import {
  TECHNOLOGY_TAB_HISTORY_ITEM_CLASS,
  TECHNOLOGY_TAB_HISTORY_LIST_CLASS,
  TECHNOLOGY_TAB_TRL_ACTIONS_GRID_CLASS,
} from '@components/pages/technology/components/shared/technology-container-query.constants';
import { Score } from '@components/shared/score.component';
import { Tile } from '@components/shared/tile.component';
import { cn } from '@lib/utils';
import { formatAssessmentDate, formatTimelineDate, getTrlTitle, isMilitaryTrlAssessment } from '@utils/assessment.util';
import { TrlSubTab } from '@utils/query-state.util';
import { LayoutDashboard, ShieldIcon, StarIcon, TrendingUp } from 'lucide-react';
import { FC, type ReactNode, useMemo } from 'react';

export const TRL1_ASSESSMENT_ID = 'trl1';

const getAssessmentItemClassName = (params: { isMilitaryTrl: boolean; isSelected: boolean }): string => {
  const { isMilitaryTrl, isSelected } = params;

  if (isMilitaryTrl) {
    return isSelected
      ? 'ring-2 ring-teal-500/80 ring-inset bg-gray-100 text-teal-700'
      : 'bg-gray-100 text-gray-600 hover:ring-2 hover:ring-teal-500/80';
  }

  return isSelected
    ? 'ring-2 ring-[#3D2EFFCC] ring-inset bg-linear-to-b bg-[#3D2EFF26] text-[#3D2EFF]'
    : 'bg-gray-100 text-gray-600 hover:ring-2 hover:ring-[#3D2EFFCC]';
};

const assessmentHistoryItemButtonClassName =
  'flex cursor-pointer items-center gap-2 rounded-md p-2 text-left transition-all duration-200';

const renderTrlTitle = (params: { isFeatured: boolean; isMilitaryTrl: boolean; title: string }): ReactNode => {
  const { isFeatured, isMilitaryTrl, title } = params;

  const titleClassName = isMilitaryTrl ? 'text-teal-700' : 'text-[#3D2EFF]';
  const starClassName = isMilitaryTrl ? 'fill-teal-600 text-teal-600' : 'fill-yellow-400 text-yellow-500';

  if (isMilitaryTrl) {
    return (
      <span className="inline-flex flex-wrap items-center gap-1 text-[12px] font-semibold">
        <span className={titleClassName}>{title}</span>
        {isFeatured && <StarIcon className={cn('size-3', starClassName)} />}
        <ShieldIcon className="size-3.5 text-teal-600" />
      </span>
    );
  }

  if (!isFeatured) {
    return <span className={cn('text-[12px] font-semibold', titleClassName)}>{title}</span>;
  }

  return (
    <span className={cn('inline-flex items-center gap-1 text-[12px] font-semibold', titleClassName)}>
      <span>{title}</span>
      <StarIcon className={cn('size-3', starClassName)} />
    </span>
  );
};

const renderAssessmentSubtitle = (params: {
  assessment: AssessmentSelectDto;
  containerGapClassName?: string;
  useClassName?: string;
}): ReactNode => {
  const { assessment, containerGapClassName, useClassName } = params;
  const assessmentDateLabel = formatAssessmentDate(assessment);

  return (
    <div
      className={cn('flex flex-col text-xs font-light leading-tight text-gray-600', containerGapClassName ?? 'gap-0.5')}
    >
      <div className="flex flex-row flex-wrap items-center gap-0.5 leading-tight">
        <span className={cn('text-gray-600', useClassName)}>{assessment.use.name}</span>
        <span className="text-gray-400">•</span>
        <span>{assessment.domain?.name}</span>
      </div>
      <span className="mt-0.5 leading-tight text-gray-500">{assessmentDateLabel}</span>
    </div>
  );
};

type TrlAssessmentHistoryListProps = {
  activeTab: TrlSubTab;
  assessments: AssessmentSelectDto[];
  featuredAssessmentId: string | null;
  isLoading?: boolean;
  onSelectAssessment: (assessmentId: string | null) => void;
  onSelectTrl1?: () => void;
  onTabChange: (tab: TrlSubTab) => void;
  selectedAssessmentId: string | null;
  showProjection?: boolean;
  timeline?: TechnologyTimeline;
};

export const TrlAssessmentHistoryList: FC<TrlAssessmentHistoryListProps> = (props) => {
  const {
    activeTab,
    assessments,
    featuredAssessmentId,
    isLoading,
    onSelectAssessment,
    onSelectTrl1,
    onTabChange,
    selectedAssessmentId,
    showProjection,
    timeline,
  } = props;

  const isOverviewActive = activeTab === 'overview';
  const isProjectionActive = activeTab === 'projection';
  const actionsGridClassName = showProjection ? TECHNOLOGY_TAB_TRL_ACTIONS_GRID_CLASS : 'grid grid-cols-1 gap-2';

  const { featuredAssessment, otherAssessments } = useMemo(() => {
    const featured = assessments.find((a) => a.id === featuredAssessmentId);
    const others = assessments.filter((a) => a.id !== featuredAssessmentId);
    return { featuredAssessment: featured, otherAssessments: others };
  }, [assessments, featuredAssessmentId]);

  const isAssessmentSelected = useMemo(() => {
    if (activeTab !== 'individual') return () => false;
    return (assessmentId: string) => {
      if (selectedAssessmentId === TRL1_ASSESSMENT_ID) return false;
      if (selectedAssessmentId === null) {
        return assessmentId === featuredAssessmentId;
      }
      return assessmentId === selectedAssessmentId;
    };
  }, [activeTab, featuredAssessmentId, selectedAssessmentId]);

  const isFeaturedActive = useMemo(() => {
    if (!featuredAssessmentId) return false;
    return isAssessmentSelected(featuredAssessmentId);
  }, [featuredAssessmentId, isAssessmentSelected]);

  const isTrl1Active = activeTab === 'individual' && selectedAssessmentId === TRL1_ASSESSMENT_ID;

  const handleSelectAssessment = (assessmentId: string) => {
    onSelectAssessment(assessmentId);
  };

  const handleSelectFeatured = () => {
    if (featuredAssessmentId) {
      onSelectAssessment(featuredAssessmentId);
    } else {
      onSelectAssessment(null);
    }
  };

  return (
    <Tile isLoading={isLoading}>
      <div className="@container/readiness-nav flex flex-col gap-3">
        <div className={actionsGridClassName}>
          {/* TRL Overview Button */}
          <button
            className={cn(
              'flex w-full items-center gap-5 rounded-lg px-3 py-2.5 text-left transition-all duration-200',
              isOverviewActive
                ? 'ring-2 ring-[#3D2EFFCC] ring-inset bg-linear-to-b bg-[#3D2EFF26] text-[#3D2EFF]'
                : 'bg-[#F5F5F5] text-[#1F1C1A] hover:ring-2 hover:ring-[#3D2EFFCC]',
            )}
            onClick={() => onTabChange('overview')}
            type="button"
          >
            <LayoutDashboard className="size-6 ml-2.5 min-h-10 shrink-0" />
            <span className="text-sm font-medium">TRL Overview</span>
          </button>

          {showProjection && (
            <button
              className={cn(
                'flex w-full items-center gap-5 rounded-lg px-3 py-2.5 text-left transition-all duration-200',
                isProjectionActive
                  ? 'ring-2 ring-[#3D2EFFCC] ring-inset bg-linear-to-b bg-[#3D2EFF26] text-[#3D2EFF]'
                  : 'bg-[#F5F5F5] text-[#1F1C1A] hover:ring-2 hover:ring-[#3D2EFFCC]',
              )}
              onClick={() => onTabChange('projection')}
              type="button"
            >
              <TrendingUp className="size-6 ml-2.5 min-h-10 shrink-0" />
              <span className="text-sm font-medium">TRL Projection</span>
            </button>
          )}
        </div>

        {/* Assessment History */}
        <div className="flex flex-col gap-2">
          <p className="font-medium text-gray-700 text-base my-1">TRL History</p>

          <div className={TECHNOLOGY_TAB_HISTORY_LIST_CLASS}>
            {/* Featured/Primary Assessment */}
            {assessments.length > 0 && featuredAssessment && (
              <PrimaryTrlAssessmentItem
                assessment={featuredAssessment}
                isSelected={isFeaturedActive}
                onSelect={handleSelectFeatured}
              />
            )}

            {/* Other Assessments */}
            {assessments.length > 0 &&
              otherAssessments.map((assessment) => (
                <AssessmentHistoryItem
                  assessment={assessment}
                  isSelected={isAssessmentSelected(assessment.id)}
                  key={assessment.id}
                  onSelect={() => handleSelectAssessment(assessment.id)}
                />
              ))}

            <DevelopmentStartedItem
              isSelected={isTrl1Active}
              onSelect={onSelectTrl1}
              timeline={timeline}
            />
          </div>
        </div>
      </div>
    </Tile>
  );
};

type PrimaryTrlAssessmentItemProps = {
  assessment: AssessmentSelectDto;
  isSelected: boolean;
  onSelect: () => void;
};

const PrimaryTrlAssessmentItem: FC<PrimaryTrlAssessmentItemProps> = (props) => {
  const { assessment, isSelected, onSelect } = props;

  const isMilitaryTrl = isMilitaryTrlAssessment({ subtype: assessment.subtype, type: assessment.type });
  const title = getTrlTitle({ subtype: assessment.subtype, type: assessment.type });
  const titleNode = renderTrlTitle({ isFeatured: assessment.featured, isMilitaryTrl, title });

  return (
    <button
      className={cn(
        assessmentHistoryItemButtonClassName,
        TECHNOLOGY_TAB_HISTORY_ITEM_CLASS,
        'min-h-10',
        getAssessmentItemClassName({ isMilitaryTrl, isSelected }),
      )}
      onClick={onSelect}
      type="button"
    >
      <Score
        colorVariant={isMilitaryTrl ? 'military' : 'default'}
        score={assessment.score}
        size="medium"
        title={titleNode}
        type="TRL"
        subtitle={renderAssessmentSubtitle({ assessment })}
      />
    </button>
  );
};

type AssessmentHistoryItemProps = {
  assessment: AssessmentSelectDto;
  isSelected: boolean;
  onSelect: () => void;
};

const AssessmentHistoryItem: FC<AssessmentHistoryItemProps> = (props) => {
  const { assessment, isSelected, onSelect } = props;

  const isMilitaryTrl = isMilitaryTrlAssessment({ subtype: assessment.subtype, type: assessment.type });
  const title = getTrlTitle({ subtype: assessment.subtype, type: assessment.type });
  const titleNode = renderTrlTitle({ isFeatured: assessment.featured, isMilitaryTrl, title });

  return (
    <button
      className={cn(
        assessmentHistoryItemButtonClassName,
        TECHNOLOGY_TAB_HISTORY_ITEM_CLASS,
        getAssessmentItemClassName({ isMilitaryTrl, isSelected }),
      )}
      onClick={onSelect}
      type="button"
    >
      <Score
        colorVariant={isMilitaryTrl ? 'military' : 'default'}
        score={assessment.score}
        size="medium"
        title={titleNode}
        type="TRL"
        subtitle={renderAssessmentSubtitle({
          assessment,
          containerGapClassName: 'gap-1',
          useClassName: 'text-gray-500',
        })}
      />
    </button>
  );
};

type DevelopmentStartedItemProps = {
  isSelected: boolean;
  onSelect?: () => void;
  timeline?: TechnologyTimeline;
};

const DevelopmentStartedItem: FC<DevelopmentStartedItemProps> = (props) => {
  const { isSelected, onSelect, timeline } = props;

  const dateLabel = timeline ? formatTimelineDate(timeline) : null;

  return (
    <button
      className={cn(
        'flex cursor-pointer items-center gap-2 rounded-md relative p-2 text-left transition-all duration-200',
        TECHNOLOGY_TAB_HISTORY_ITEM_CLASS,
        isSelected
          ? 'ring-2 ring-[#3D2EFFCC] ring-inset bg-linear-to-b bg-[#3D2EFF26] text-[#3D2EFF]'
          : 'bg-gray-100 text-gray-600 hover:ring-2 hover:ring-[#3D2EFFCC]',
      )}
      onClick={onSelect}
      type="button"
    >
      <Score
        score={1}
        size="medium"
        subtitle={
          <div className="flex flex-col gap-0.5 text-xs font-light leading-tight text-gray-600">
            <span className="leading-tight">Development Started</span>
            <span className="text-gray-500 leading-tight">{dateLabel ?? 'Not Set'}</span>
          </div>
        }
        type="TRL"
        title="TRL 1"
        className="bg-blue-100"
      />
    </button>
  );
};
